import { ResponsiveDataTable } from '@/ui/organisms/responsive-data-table/ResponsiveDataTable'
import { GlobalLeaderboard } from '@/features/points/logic/useLeaderboard'
import { Leaderboard } from '../../logic/useSeasonLeaderboard'
import { useAccount } from '@/domain/hooks/useAccount'
import { Typography } from '@/ui/atoms/typography/Typography'
import { CopyButton } from '@/ui/atoms/copy-button/CopyButton'
import { PointsCell } from '@/ui/molecules/data-table/components/PointsCell'
import { HIDE_POINTS_SECTION } from '../../views/PointsView'
import { Badge } from '@/ui/atoms/badge/Badge'
import { cn } from '@/ui/utils/style'

interface BuddiesTableProps {
  buddiesLeaderboard?: GlobalLeaderboard
}

export function BuddiesTable({ buddiesLeaderboard }: BuddiesTableProps) {
  const account = useAccount()

  return (
    <div className="w-full">
      {buddiesLeaderboard?.entries?.length ? (
        <div className="w-full">
          <ResponsiveDataTable
            gridTemplateColumnsClassName="grid-cols-[84px_2fr_2fr_1fr]"
            columnDefinition={{
              week: {
                header: 'Ranking',
                headerAlign: 'left',
                showOnMobile: true,
                sortable: true,
                sortingFn: (a, b) => a.original.rank - b.original.rank,
                renderCell: ({ rank }: Leaderboard) => (
                  <div>
                    <div className="flex w-full flex-row justify-start pl-3 text-sm">{rank || 0}</div>
                  </div>
                ),
              },
              user: {
                header: 'User',
                headerAlign: 'left',
                showOnMobile: true,
                renderCell: ({ wallet_address }) => (
                  <div>
                    <div className="flex w-full flex-row items-center gap-4 text-sm">
                      <p>
                        {wallet_address.slice(0, 7)}...{wallet_address.slice(-4)}
                      </p>
                      {wallet_address.toLowerCase() === account?.toLowerCase() && (
                        <Badge className="cursor-not-allowed">You</Badge>
                      )}
                    </div>
                  </div>
                ),
              },
              buddyCode: {
                header: 'Buddy code',
                headerAlign: 'left',
                showOnMobile: false,
                renderCell: ({ referrals }, mobileViewOptions) => {
                  if (mobileViewOptions?.isMobileView) {
                    return (
                      <div
                        className={cn(
                          'flex flex-row items-center',
                          mobileViewOptions.showOnMobile ? 'justify-center' : 'justify-between',
                        )}
                      >
                        {!mobileViewOptions.showOnMobile && (
                          <Typography variant="prompt" className="w-full">
                            {mobileViewOptions.rowTitle}
                          </Typography>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">{referrals.code}</span>
                          <CopyButton text={referrals.code} />
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{referrals.code}</span>
                      <CopyButton text={referrals.code} />
                    </div>
                  )
                },
              },
              totalReferrals: {
                header: 'Total buddies',
                headerAlign: 'right',
                showOnMobile: true,
                sortable: true,
                sortingFn: (a, b) => a.original.referrals.total - b.original.referrals.total,
                renderCell: ({ referrals }, mobileViewOptions) => (
                  <PointsCell
                    points={referrals.total}
                    mobileViewOptions={mobileViewOptions}
                    className="justify-center md:justify-end"
                    gradient={true}
                  />
                ),
              },
              ...(!HIDE_POINTS_SECTION
                ? {
                    points: {
                      header: 'Points',
                      headerAlign: 'left',
                      sortable: true,
                      sortingFn: (a, b) => a.original.points.total.distributed - b.original.points.total.distributed,
                      renderCell: ({ points }, mobileViewOptions) => (
                        <PointsCell
                          points={points.total.distributed}
                          mobileViewOptions={mobileViewOptions}
                          className="md:justify-start"
                        />
                      ),
                    },
                  }
                : {}),
              ...(!HIDE_POINTS_SECTION
                ? {
                    totalPoints: {
                      header: 'Total points',
                      headerAlign: 'right',
                      showOnMobile: true,
                      sortable: true,
                      sortingFn: (a, b) => a.original.points.total.raw - b.original.points.total.raw,
                      renderCell: ({ points }, mobileViewOptions) => (
                        <PointsCell
                          points={points.total.raw}
                          mobileViewOptions={mobileViewOptions}
                          className="justify-center md:justify-end"
                          gradient={true}
                        />
                      ),
                    },
                  }
                : {}),
            }}
            data={buddiesLeaderboard?.entries || []}
          />
        </div>
      ) : (
        <div className="flex w-full flex-col items-center justify-center gap-2 py-10 text-center">
          <Typography gradient={true} variant="h3">
            Leaderboard is currently empty. Check back soon!
          </Typography>
        </div>
      )}
    </div>
  )
}
